import React from 'react';
import {
    clearStorage,
    getBlackList,
    getPulseShownCountStorage,
    addToDoListWidgetPortlet,
    updateBlackList
} from '../actions/feature';
import texts, {getServerTexts} from '../texts';
import analyticsTexts from '../../ReportGenerator/texts';
import {PopoverPlacement} from '@priority-software/priority-style-react';
import {startForm} from '../../../_shared/GWTBridge/invoke/netForm';
import reportsTexts from '../../ReportsDialog/texts';
import {texts as textConst, getLocalizedText} from '../../../constants/TextConst';
import {isRTL} from '../../../Localization';
import {
    getHasAnalyticsPermissions,
    startProcWithActivationSource
} from '../../../_shared/GWTBridge/invoke/genPage';
import {ISRAEL_TAX_REGULATION_BLOCKED_BY_USER} from './featureConsts';
import {default as toDoListPromoTexts} from '../../ToDoList/texts';
import {TodoListPromoContent} from '../components/ToDoListPromo';
import {showSuccessToast} from '../../../components/Toast/actions/ToastActions';
import {monitorAction} from '../../../services/datadog';
import {ToDoListPromoMonitoring} from '../../../constants/monitoring';

export interface FeatureProp {
    title?: string;
    content?: JSX.Element;
    visualContent?: string[]; //image src
    primaryButtonText?: string;
    secondaryButtonText?: string;
    placement: PopoverPlacement;
    confirmButtonHandler?: () => void;
    showMask?: boolean;
    large?: boolean;
    isPrimaryCloseButton?: boolean;
    clickOutSideClose?: boolean;
    closeOnEsc?: boolean;
    forceBlacklistOnClickoutside?: boolean;
    zIndex?: number;
    ignoreLocalBlacklist?: boolean;
}

//white list
export const featureMap = new Map<string, FeatureProp>();
export let blacklist: string[] = [];
export let featurePulseCount = {};

export const featuresIDs = {
    creditCardReconID: 'creditCardRecon',
    filters: 'Pulse_Filter',
    bankRecon: 'bank_recon',
    accRecon: 'acc_recon',
    automationsFeature: 'automations_feature',
    automationsFromNewMenu: 'automations_menu_new',
    automationsFromOldMenu: 'automations_menu_old',
    excelTemplates: 'excel_templates',
    moreInfo: 'more_info',
    analytics: 'AnalyticsPromo',
    analytics2: 'AnalyticsPromo2',
    sidePanelAttachments: 'sidePanelAttachments',
    sidePanelHistory: 'sidePanelHistory',
    exportInvoiceEOL: 'exportInvoiceEOL',
    chartProcessWizard: 'chartProcessWizard',
    israelTaxRegulation: 'israelTaxRegulation',
    toDoListWidget: 'toDoListWidget'
};

const getFeatureJson = () => ({
    [featuresIDs.creditCardReconID]: {
        title: texts.creditCardRecon_title,
        content: <>{texts.creditCardRecon_content}</>,
        secondaryButtonText: texts.creditCardRecon_cbutton,
        primaryButtonText: texts.notNowBtn,
        placement: PopoverPlacement.right,
        confirmButtonHandler: () => {
            window.open('https://support.priority-software.com/#/kb/LB18000062/Hebrew', '_blank');
        },
        isPrimaryCloseButton: true,
        zIndex: 10009
    },
    [featuresIDs.bankRecon]: {
        title: texts.bankRecon_Title,
        content: <>{texts.bankRecon_content}</>,
        primaryButtonText: texts.bankRecon_cbutton,
        secondaryButtonText: texts.NotNow,
        placement: PopoverPlacement.rightStart,
        confirmButtonHandler: () => startForm('BANKRECONSP')
    },
    [featuresIDs.accRecon]: {
        title: texts.accReconn_Title,
        content: <>{texts.accRecon_content}</>,
        primaryButtonText: texts.bankRecon_cbutton,
        secondaryButtonText: texts.NotNow,
        placement: PopoverPlacement.rightStart,
        confirmButtonHandler: () => startForm('ACCRECONSP')
    },
    [featuresIDs.automationsFeature]: {
        title: texts.automations_feature_title,
        content: (
            <>
                <div>{texts.automations_content}</div>
            </>
        ),
        primaryButtonText: texts.gotIt,
        placement: PopoverPlacement.bottom,
        clickOutSideClose: true
    },
    [featuresIDs.automationsFromNewMenu]: {
        title: texts.automations_newMenu_title,
        content: (
            <>
                <div>{texts.automations_content}</div>
            </>
        ),
        primaryButtonText: texts.gotIt,
        placement: PopoverPlacement.bottom,
        ignoreBlackList: true,
        clickOutSideClose: true,
        ignoreLocalBlacklist: true
    },
    [featuresIDs.automationsFromOldMenu]: {
        title: texts.automation_oldMenu_title,
        content: (
            <>
                <div>{texts.automation_oldMenu_content}</div>
            </>
        ),
        primaryButtonText: texts.gotIt,
        placement: PopoverPlacement.bottom,
        ignoreBlackList: true,
        clickOutSideClose: true,
        ignoreLocalBlacklist: true
    },
    [featuresIDs.excelTemplates]: {
        title: reportsTexts.selectExcelTemplateTitle,
        content: <>{reportsTexts.selectExcelTemplate}</>,
        primaryButtonText: reportsTexts.approve,
        placement: PopoverPlacement.left
    },
    [featuresIDs.moreInfo]: {
        placement: PopoverPlacement.topEndStart,
        clickOutSideClose: true,
        oncePerForm: true
    },
    [featuresIDs.analytics]: {
        title: analyticsTexts.promoTitle,
        content: (
            <>
                <div>{analyticsTexts.promoContent}</div>
            </>
        ),
        primaryButtonText: analyticsTexts.promoShowBtn,
        secondaryButtonText: analyticsTexts.promoOkBtn,
        placement: PopoverPlacement.topStart,
        clickOutSideClose: false,
        oncePerForm: true,
        forceBlacklistOnClickoutside: true
    },
    [featuresIDs.analytics2]: {
        title: analyticsTexts.promoTitle,
        content: (
            <>
                <div>{analyticsTexts.promoContent}</div>
            </>
        ),
        primaryButtonText: getHasAnalyticsPermissions()
            ? analyticsTexts.promoShowBtn
            : analyticsTexts.promoOkBtn,
        secondaryButtonText: getHasAnalyticsPermissions() ? analyticsTexts.promoOkBtn : null,
        placement: PopoverPlacement.left,
        clickOutSideClose: true,
        oncePerForm: true,
        closeOnEsc: true,
        forceBlacklistOnClickoutside: true,
        zIndex: 10009
    },
    [featuresIDs.sidePanelAttachments]: {
        placement: PopoverPlacement.leftStart,
        clickOutSideClose: true,
        oncePerForm: false
    },
    [featuresIDs.sidePanelHistory]: {
        placement: PopoverPlacement.leftStart,
        clickOutSideClose: true,
        oncePerForm: false
    },
    [featuresIDs.exportInvoiceEOL]: {
        title: getLocalizedText(textConst.finvoicesEolTitle),
        content: (
            <>
                <div>{exportInvoiceEolFallback()}</div>
            </>
        ),
        primaryButtonText: getLocalizedText(textConst.finvoicesEolOkButton),
        secondaryButtonText: getLocalizedText(textConst.finvoicesEolNotNowButton),
        placement: PopoverPlacement.rightStart,
        clickOutSideClose: true,
        oncePerForm: false,
        closeOnEsc: true,
        ignoreBlackList: true,
        ignoreLocalBlacklist: true,
        confirmButtonHandler: () => startForm('CINVOICES')
    },
    [featuresIDs.chartProcessWizard]: {
        placement: PopoverPlacement.bottom,
        clickOutSideClose: true
    },
    [featuresIDs.israelTaxRegulation]: {
        title: texts.israelTaxRegulationTitle,
        content: <>{israelTaxRegulationContent()}</>,
        primaryButtonText: texts.israelTaxRegulationSecondaryButton,
        secondaryButtonText: texts.israelTaxRegulationPrimaryButton,
        isPrimaryCloseButton: true,
        placement: PopoverPlacement.rightStart,
        confirmButtonHandler: () => {
            window.localStorage.setItem(ISRAEL_TAX_REGULATION_BLOCKED_BY_USER, 'true');
            startProcWithActivationSource('TAXESILLOAD', 0);
        },
        clickOutSideClose: true,
        ignoreBlackList: true,
        ignoreLocalBlacklist: true,
        oncePerForm: false
    },
    [featuresIDs.toDoListWidget]: {
        title: toDoListPromoTexts.promos_title,
        content: <TodoListPromoContent text={toDoListPromoTexts.promos_content} />,
        primaryButtonText: toDoListPromoTexts.promo_decline,
        secondaryButtonText: toDoListPromoTexts.promo_approve,
        placement: PopoverPlacement.rightStart,
        isPrimaryCloseButton: true,
        ignoreBlackList: true,
        ignoreLocalBlacklist: true,
        confirmButtonHandler: async () => {
            await addToDoListWidgetPortlet();
            showSuccessToast(toDoListPromoTexts.promos_toast_msg);
            updateBlackList(featuresIDs.toDoListWidget);
            monitorAction(ToDoListPromoMonitoring.accept);
        },
        onClosePopover: isBtnClick => {
            if (isBtnClick) {
                updateBlackList(featuresIDs.toDoListWidget);
                monitorAction(ToDoListPromoMonitoring.decline);
            } else {
                monitorAction(ToDoListPromoMonitoring.close);
            }
        }
    }
});

export const getFeatureIgnoreBlackList = featureID => {
    return getFeatureJson()[featureID];
};

export const shouldShowFeature = featureID => {
    return featureMap.has(featureID) && featurePulseCount[featureID] !== 6;
};

export async function initFeatureMap() {
    if (window.location.search.includes('_clearStorage')) {
        clearStorage();
    }

    if (Object.keys(featurePulseCount).length === 0) {
        featurePulseCount = await getPulseShownCountStorage();
    }

    await getServerTexts();

    const featureJson = getFeatureJson();
    if (featureMap.size === 0) {
        try {
            const ids = await getBlackList();
            blacklist = [...ids];
            const featureIds = Object.keys(featureJson);
            featureIds.forEach(id => {
                if (blacklist.length === 0 || !blacklist.includes(id)) {
                    featureMap.set(id, featureJson[id]);
                }
            });
        } catch (error) {
            console.log('error: ', error);
        }
    }
}

export const updateLocalBlackList = id => {
    featureMap.delete(id);
};

const exportInvoiceEolFallback = () => {
    if (getLocalizedText(textConst.finvoicesEolMsg)) {
        return getLocalizedText(textConst.finvoicesEolMsg);
    } else {
        const fallBackText = isRTL()
            ? 'החל מגרסה 24.1 מסך חשבוניות חו"ל לא יהיה זמין יותר בחברה שפועלת מחוץ לישראל. \n כדאי להתחיל כבר עכשיו להשתמש במסך חשבוניות מרכזות המותאם לעבודה מחוץ לישראל.'
            : 'We recommend using Multi-Shipment Invoices instead of Export Invoices. \n In version 24.1 the Export Invoices form will no longer be available. See how the Multi-Shipment Invoices form is shaped to your needs.';
        return fallBackText;
    }
};

const israelTaxRegulationContent = () => {
    return (
        <>
            <div>{texts.israelTaxRegulationContent}</div>
            <li>{texts.israelTaxRegulationList1}</li>
            <li>{texts.israelTaxRegulationList2}</li>
            <div>{texts.israelTaxRegulationContent2}</div>
        </>
    );
};
